import { useLayoutEffect, useRef, useState } from "react";
import PageBackdrop from "../ui/PageBackdrop";
import PageHeader from "../ui/PageHeader";
import { useChatSocket } from "../../services/chat.service";
import ChatMessage from "./chat-message.component";
import ChatProgress from "./chat-progress.component";

const Chat = () => {
  const { messages, progress, streamingText, isWaiting, connected, sendMessage, startFresh } =
    useChatSocket();
  const [input, setInput] = useState("");
  const [clearing, setClearing] = useState(false);
  const [clearError, setClearError] = useState("");
  const stickToBottomRef = useRef(true);

  // useLayoutEffect + declared BEFORE the follow effect on purpose: layout
  // effects run in order, so the stick flag is correctly initialized before
  // the follow effect below reads it on mount. (A passive useEffect here
  // would run after, leaving the ref at its optimistic `true` for the first
  // follow pass and yanking scrolled-up readers on mount.)
  useLayoutEffect(() => {
    const updateStick = () => {
      const el = document.scrollingElement || document.documentElement;
      stickToBottomRef.current =
        el.scrollHeight - (el.scrollTop + el.clientHeight) < 120;
    };
    updateStick();
    window.addEventListener("scroll", updateStick, { passive: true });
    return () => window.removeEventListener("scroll", updateStick);
  }, []);

  useLayoutEffect(() => {
    // Only follow new content while the user is already near the bottom -
    // unconditional scrolling yanked the window on every progress tick and
    // every agent message, even mid-read. useLayoutEffect (not useEffect) on
    // purpose: it runs synchronously after DOM mutation but BEFORE paint, so
    // the browser never flashes the un-scrolled frame - the post-paint snap
    // was the visible "shift up" on every message. Explicit scrollTop math
    // instead of scrollIntoView: the sentinel sat above the sticky input bar,
    // so block:"end" alignment fought the sticky positioning every snap.
    // Instant (no smooth animation): streaming deltas arrive many times a
    // second, and stacking smooth animations fights the growing height.
    if (stickToBottomRef.current) {
      const el = document.scrollingElement || document.documentElement;
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, progress, streamingText]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim() || isWaiting) return;
    stickToBottomRef.current = true;
    sendMessage(input);
    setInput("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleFresh = async () => {
    if (clearing) return;
    setClearing(true);
    setClearError("");
    try {
      await startFresh();
    } catch {
      setClearError("Couldn't clear history. Try again.");
    } finally {
      setClearing(false);
    }
  };

  return (
    <>
      <style>{`
        *, *::before, *::after { box-sizing: border-box; }
        .cc-root { background: var(--bg); min-height: 100vh; font-family: var(--font-sans); color: var(--text-primary); position: relative; display: flex; flex-direction: column; }
        .cc-inner { position: relative; z-index: 1; max-width: 860px; width: 100%; margin: 0 auto; padding: 52px 24px 0; flex: 1; display: flex; flex-direction: column; }

        .cc-header { opacity: 0; animation: cc-fadeUp 0.45s 0.05s ease forwards; }
        @keyframes cc-fadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }

        .cc-toolbar { display: flex; align-items: center; justify-content: flex-end; gap: 12px; padding: 4px 0 16px; opacity: 0; animation: cc-fadeUp 0.45s 0.12s ease forwards; }
        .cc-fresh-btn { font-family: var(--font-mono); font-size: 12px; letter-spacing: 0.04em; color: var(--text-tertiary); background: transparent; border: 1px solid var(--border-subtle); border-radius: var(--radius-pill); padding: 6px 14px; cursor: pointer; transition: all var(--dur-fast) var(--ease-out); white-space: nowrap; }
        .cc-fresh-btn:hover:not(:disabled) { border-color: var(--border-strong); color: var(--text-primary); background: rgba(255,255,255,0.03); }
        .cc-fresh-btn:disabled { opacity: 0.35; cursor: not-allowed; }
        .cc-fresh-error { font-family: var(--font-mono); font-size: 12px; color: var(--negative); letter-spacing: 0.03em; }

        .cc-messages { flex: 1; padding-bottom: var(--space-8); }

        .cc-empty { text-align: center; padding: 72px 0; opacity: 0; animation: cc-fadeUp 0.45s 0.18s ease forwards; }
        .cc-empty-icon { width: 48px; height: 48px; margin: 0 auto 16px; display: flex; align-items: center; justify-content: center; border: 1px solid var(--border-subtle); border-radius: var(--radius-md); background: var(--surface-1); color: var(--text-tertiary); }
        .cc-empty-title { font-family: var(--font-mono); font-size: 12px; color: var(--text-tertiary); letter-spacing: 0.06em; text-transform: uppercase; margin-bottom: 6px; }
        .cc-empty-sub { font-size: 14px; color: var(--text-tertiary); font-weight: 400; line-height: 1.6; }

        .cc-input-outer { position: sticky; bottom: 0; background: linear-gradient(180deg, transparent, var(--bg) 40%); padding: var(--space-6) 0 var(--space-6); }
        .cc-input-wrap {
          display: flex;
          align-items: flex-end;
          background: var(--surface-1);
          border: 1px solid var(--border-subtle);
          border-radius: var(--radius-md);
          padding: 10px 10px 10px 18px;
          gap: 10px;
          transition: border-color var(--dur-base) var(--ease-out), box-shadow var(--dur-base) var(--ease-out);
        }
        .cc-input-wrap:focus-within { border-color: var(--accent-border); box-shadow: 0 0 0 3px rgba(52,211,153,0.07); }

        .cc-textarea {
          flex: 1; min-width: 0; background: transparent; border: none; outline: none; resize: none;
          font-family: var(--font-sans); font-size: 15px; line-height: 1.5;
          color: var(--text-primary); max-height: 160px; padding: 6px 0;
        }
        .cc-textarea::placeholder { color: var(--text-tertiary); }

        .cc-send-btn {
          background: var(--accent); color: var(--accent-ink); border: none; cursor: pointer;
          font-family: var(--font-sans); font-size: 13px; font-weight: 600;
          padding: 10px 20px; border-radius: var(--radius-sm);
          transition: background var(--dur-fast) var(--ease-out), transform var(--dur-fast) var(--ease-out);
          white-space: nowrap; flex-shrink: 0;
        }
        .cc-send-btn:hover:not(:disabled) { background: var(--accent-hover); transform: translateY(-1px); }
        .cc-send-btn:disabled { opacity: 0.4; cursor: not-allowed; }

        .cc-status { font-family: var(--font-mono); font-size: 11px; color: var(--text-quaternary); text-align: center; padding-bottom: var(--space-3); letter-spacing: 0.04em; }

        @media (max-width: 500px) {
          .cc-inner { padding: 32px 16px 0; }
        }
      `}</style>

      <div className="cc-root">
        <PageBackdrop />
        <div className="cc-inner">
          <div className="cc-header">
            <PageHeader
              eyebrow="Chat"
              title={
                <>
                  Ask Max Stock <em>Machine.</em>
                </>
              }
              description="Ask about any ticker, run a full analysis, or explore the screener - the agent decides which tools it needs."
            />
          </div>

          <div className="cc-toolbar">
            {clearError && <span className="cc-fresh-error">{clearError}</span>}
            <button
              type="button"
              className="cc-fresh-btn"
              onClick={handleFresh}
              disabled={messages.length === 0 || isWaiting || clearing}
            >
              {clearing ? "Clearing…" : "↺ Start fresh"}
            </button>
          </div>

          <div className="cc-messages">
            {messages.length === 0 && !isWaiting && (
              <div className="cc-empty">
                <div className="cc-empty-icon">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M4 15V6a1 1 0 011-1h10a1 1 0 011 1v7a1 1 0 01-1 1H8l-4 4z"
                      stroke="#4a5568"
                      strokeWidth="1.4"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <p className="cc-empty-title">No messages yet</p>
                <p className="cc-empty-sub">
                  Try "What's AAPL trading at?" or "Run a full analysis on MSFT."
                </p>
              </div>
            )}

            {messages.map((m, i) => (
              <ChatMessage key={i} role={m.role} type={m.type} content={m.content} />
            ))}

            {isWaiting && streamingText === null && <ChatProgress text={progress} />}

            {streamingText !== null && (
              <ChatMessage role="assistant" type="message" content={streamingText} />
            )}
          </div>

          <div className="cc-input-outer">
            {!connected && <div className="cc-status">Connecting…</div>}
            <form className="cc-input-wrap" onSubmit={handleSubmit}>
              <textarea
                className="cc-textarea"
                placeholder="Message Max Stock Machine…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={1}
              />
              <button
                type="submit"
                className="cc-send-btn"
                disabled={!input.trim() || isWaiting || !connected}
              >
                {isWaiting ? "Working…" : "Send"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default Chat;
